/**
 * messaging.js — Logica di alto livello per send/receive di messaggi.
 *
 * Cifra/decifra client-side. Il server vede solo blob.
 */

import { callFn, db } from './firebase';
import {
  doc,
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  getDoc,
  getDocs,
} from 'firebase/firestore';
import {
  boxEncrypt,
  boxDecrypt,
  toBase64Url,
  fromBase64Url,
} from './crypto';
import {
  compressImage,
  validateVideo,
  generateAttachmentKey,
  encryptBlobToStream,
  uploadEncryptedBlob,
  ensureSodium,
} from './attachments';

const MAX_FILE_BYTES = 75 * 1024 * 1024; // 75MB (regola Storage: <80MB)

/**
 * Invia un messaggio cifrato.
 *
 * @param {object} params
 * @param {string} params.text - testo in chiaro da cifrare
 * @param {object} params.recipient - { uid, pubKeyBox (base64url) }
 * @param {Uint8Array} params.mySecKeyBox
 * @param {number} params.senderTTL - 10|300|3600|0|-1
 * @param {object} [params.security] - { senderSecLvl?, showChatSec? }
 */
export async function sendEncryptedMessage({ text, recipient, mySecKeyBox, myBoxPub, senderTTL, security, senderSigil, recipientSigil }) {
  if (!text || text.length === 0) throw new Error('TEXT_EMPTY');
  if (text.length > 500) throw new Error('TEXT_TOO_LONG');

  const recipientPubKeyBytes = await fromBase64Url(recipient.pubKeyBox);
  const { ciphertext, nonce } = await boxEncrypt(text, recipientPubKeyBytes, mySecKeyBox);

  const payload = {
    recipientUid: recipient.uid,
    ciphertext: await toBase64Url(ciphertext),
    nonce: await toBase64Url(nonce),
    senderTTL,
  };
  // Sprint Premium L2: se sto inviando da un sigil derivato, lo dichiaro al
  // server cosi' il chatId e' coerente con le chat in arrivo su quel sigil.
  if (senderSigil) payload.senderSigil = senderSigil;
  if (recipientSigil) payload.recipientSigil = recipientSigil;
  // Copia-per-sé: cifro lo stesso testo verso la MIA chiave (box-to-self), così
  // potrò rileggere ciò che ho inviato. Solo se l'opzione è attiva.
  const self = await encryptForSelf(text, myBoxPub, mySecKeyBox);
  if (self) { payload.selfCipher = self.cipher; payload.selfNonce = self.nonce; }
  if (security?.senderSecLvl !== undefined) payload.senderSecLvl = security.senderSecLvl;
  if (security?.showChatSec !== undefined) payload.showChatSec = security.showChatSec;

  const fn = callFn('sendMessage');
  const res = await fn(payload);
  return res.data;
}

// ── Messaggi rileggibili dal mittente (copia-per-sé) ──────────────────────
const SELF_READABLE_KEY = 'nullum.selfReadable.v1';

/** Preferenza: il mittente può rileggere i propri messaggi? (default: sì) */
export function isSelfReadable() {
  try { return localStorage.getItem(SELF_READABLE_KEY) !== '0'; } catch { return true; }
}
export function setSelfReadable(on) {
  try { localStorage.setItem(SELF_READABLE_KEY, on ? '1' : '0'); } catch {}
}

/**
 * Cifra un testo verso la PROPRIA chiave (box-to-self) per rileggerlo dopo.
 * Ritorna null se l'opzione è disattivata o manca la chiave.
 * @returns {Promise<{cipher:string,nonce:string}|null>}
 */
export async function encryptForSelf(text, myBoxPub, mySecKeyBox) {
  if (!isSelfReadable() || !myBoxPub || !mySecKeyBox || !text) return null;
  try {
    const myPub = typeof myBoxPub === 'string' ? await fromBase64Url(myBoxPub) : myBoxPub;
    const { ciphertext, nonce } = await boxEncrypt(text, myPub, mySecKeyBox);
    return { cipher: await toBase64Url(ciphertext), nonce: await toBase64Url(nonce) };
  } catch { return null; }
}

/**
 * Decifra la copia-per-sé di un mio messaggio.
 * @returns {Promise<string|null>}
 */
export async function decryptSelfMessage(msg, myBoxPub, mySecKeyBox) {
  if (!msg?.selfCipher || !msg?.selfNonce || !myBoxPub || !mySecKeyBox) return null;
  try {
    const myPub = typeof myBoxPub === 'string' ? await fromBase64Url(myBoxPub) : myBoxPub;
    const ct = await fromBase64Url(msg.selfCipher);
    const nonce = await fromBase64Url(msg.selfNonce);
    return await boxDecrypt(ct, nonce, myPub, mySecKeyBox);
  } catch { return null; }
}

/**
 * Invia un'immagine come allegato cifrato end-to-end.
 *
 * Flusso: comprimi → cifra il blob con chiave XChaCha random → "incarta" la
 * chiave per il destinatario (box) → sendMessage con metadati allegato →
 * carica il blob cifrato su Storage al path deterministico chatId/msgId.
 *
 * @param {object} p
 * @param {File|Blob} p.imageFile
 * @param {string} [p.caption]
 * @param {object} p.recipient { uid, pubKeyBox }
 * @param {Uint8Array} p.mySecKeyBox
 * @param {number} p.senderTTL
 */
export async function sendEncryptedAttachment({ file, caption, recipient, mySecKeyBox, myBoxPub, senderTTL, senderSigil, recipientSigil, downloadable }) {
  await ensureSodium();
  const mime = file.type || 'application/octet-stream';

  // 1. prepara il blob a seconda del tipo
  let blob, kind, attachmentName = null, outMime;
  if (mime.startsWith('image/')) {
    blob = await compressImage(file); // → jpeg
    kind = 'image'; outMime = 'image/jpeg';
  } else if (mime.startsWith('video/')) {
    await validateVideo(file);        // ≤10s, ≤12MB (throws altrimenti)
    blob = file; kind = 'video'; outMime = mime;
  } else if (mime.startsWith('audio/')) {
    if (file.size > MAX_FILE_BYTES) throw new Error('AUDIO_TOO_LARGE (max 75MB)');
    blob = file; kind = 'audio'; outMime = mime;
  } else {
    if (file.size > MAX_FILE_BYTES) throw new Error('FILE_TOO_LARGE (max 75MB)');
    blob = file; kind = 'file'; outMime = mime;
    attachmentName = (file.name || 'file').slice(0, 120);
  }

  // 2. chiave random + cifratura blob
  const fileKey = generateAttachmentKey();
  const { encryptedBlob } = await encryptBlobToStream(blob, fileKey);
  // 3. incarta la chiave del file per il destinatario
  const recipientPub = await fromBase64Url(recipient.pubKeyBox);
  const fileKeyB64 = await toBase64Url(fileKey);
  const wrapped = await boxEncrypt(fileKeyB64, recipientPub, mySecKeyBox);
  // 3b. incarta la chiave ANCHE per sé (così il MITTENTE può riaprire il proprio
  //     allegato), seguendo l'impostazione "Rileggi i miei messaggi".
  const selfKey = await encryptForSelf(fileKeyB64, myBoxPub, mySecKeyBox);
  // 4. didascalia (cifrata) — se vuota usa un segnaposto in base al tipo
  const placeholder = kind === 'image' ? '📷' : kind === 'video' ? '🎬' : `📎 ${attachmentName || 'file'}`;
  const capText = (caption && caption.trim()) ? caption.trim().slice(0, 500) : placeholder;
  const cap = await boxEncrypt(capText, recipientPub, mySecKeyBox);
  const self = await encryptForSelf(capText, myBoxPub, mySecKeyBox);
  // 5. crea il messaggio con i metadati allegato
  const res = await callFn('sendMessage')({
    recipientUid: recipient.uid,
    ciphertext: await toBase64Url(cap.ciphertext),
    nonce: await toBase64Url(cap.nonce),
    senderTTL,
    ...(senderSigil ? { senderSigil } : {}),
    ...(recipientSigil ? { recipientSigil } : {}),
    ...(downloadable ? { downloadable: true } : {}),
    kind,
    attachmentMime: outMime,
    ...(attachmentName ? { attachmentName } : {}),
    attachmentKeyCipher: await toBase64Url(wrapped.ciphertext),
    attachmentKeyNonce: await toBase64Url(wrapped.nonce),
    ...(self ? { selfCipher: self.cipher, selfNonce: self.nonce } : {}),
    ...(selfKey ? { attachmentKeySelfCipher: selfKey.cipher, attachmentKeySelfNonce: selfKey.nonce } : {}),
  });
  const data = res.data;
  if (!data?.chatId || !data?.msgId) throw new Error(data?.error || 'SEND_FAILED');
  // 6. carica il blob cifrato (la chat ora esiste → le regole Storage consentono)
  try {
    await uploadEncryptedBlob(data.chatId, data.msgId, encryptedBlob, kind);
  } catch (e) {
    // Errore tipico: superato il limite Storage rules, CORS non aggiornato,
    // permessi rules. Annulliamo il msg lato server cosi' il destinatario
    // non vede un bubble fantasma e l'errore viene risalito al chiamante.
    console.error('upload blob failed', e, 'kind=', kind, 'size=', encryptedBlob?.size);
    try { await callFn('unsendMessage')({ chatId: data.chatId, msgId: data.msgId }); } catch {}
    const code = e?.code || '';
    const msg = e?.message || String(e);
    throw new Error(`UPLOAD_FAILED (${code || 'storage'}): ${msg}`);
  }
  return data;
}

/**
 * Estrae (scarta) la chiave del file da un messaggio allegato ricevuto.
 * @returns {Promise<Uint8Array>} la chiave XChaCha per decifrare il blob
 */
export async function unwrapAttachmentKey(msg, mySecKeyBox, senderPubKeyBox) {
  const ct = await fromBase64Url(msg.attachmentKeyCipher);
  const nonce = await fromBase64Url(msg.attachmentKeyNonce);
  const senderPub = await fromBase64Url(senderPubKeyBox);
  const keyB64 = await boxDecrypt(ct, nonce, senderPub, mySecKeyBox); // stringa base64url
  return await fromBase64Url(keyB64);
}

/**
 * Scarta la chiave del file dalla copia incartata PER SE STESSI (per riaprire
 * un PROPRIO allegato). Richiede che il messaggio abbia attachmentKeySelf*.
 * @returns {Promise<Uint8Array>}
 */
export async function unwrapAttachmentKeySelf(msg, myBoxPub, mySecKeyBox) {
  if (!msg?.attachmentKeySelfCipher || !msg?.attachmentKeySelfNonce) {
    throw new Error('NO_SELF_ATTACHMENT_KEY');
  }
  const myPub = typeof myBoxPub === 'string' ? await fromBase64Url(myBoxPub) : myBoxPub;
  const ct = await fromBase64Url(msg.attachmentKeySelfCipher);
  const nonce = await fromBase64Url(msg.attachmentKeySelfNonce);
  const keyB64 = await boxDecrypt(ct, nonce, myPub, mySecKeyBox);
  return await fromBase64Url(keyB64);
}

/**
 * Decifra un messaggio ricevuto.
 *
 * @param {object} msg - documento Firestore con {ciphertext, nonce, senderUid}
 * @param {Uint8Array} mySecKeyBox
 * @param {string} senderPubKeyBox base64url
 * @returns {Promise<string>} testo in chiaro
 */
export async function decryptMessage(msg, mySecKeyBox, senderPubKeyBox) {
  const ct = await fromBase64Url(msg.ciphertext);
  const nonce = await fromBase64Url(msg.nonce);
  const senderPubBytes = await fromBase64Url(senderPubKeyBox);
  try {
    return await boxDecrypt(ct, nonce, senderPubBytes, mySecKeyBox);
  } catch (_e) {
    return '[messaggio illeggibile]';
  }
}

/**
 * Osserva i messaggi di una chat in tempo reale.
 *
 * @returns funzione di unsubscribe
 */
export function subscribeChatMessages(chatId, onUpdate, onError) {
  const q = query(
    collection(db(), 'chats', chatId, 'messages'),
    orderBy('createdAt', 'asc'),
    limit(200)
  );
  return onSnapshot(q, (snap) => {
    const msgs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    onUpdate(msgs);
  }, onError);
}

/**
 * Legge una tantum i messaggi di una chat (non realtime).
 * @returns {Promise<Array>} messaggi ordinati per createdAt asc
 */
export async function fetchChatMessages(chatId, max = 200) {
  const q = query(
    collection(db(), 'chats', chatId, 'messages'),
    orderBy('createdAt', 'asc'),
    limit(max),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Osserva la lista delle chat dell'utente in tempo reale.
 */
export function subscribeChatList(myUid, onUpdate, onError) {
  const q = query(
    collection(db(), 'chats'),
    where('participants', 'array-contains', myUid),
    orderBy('lastMessageAt', 'desc'),
    limit(50)
  );
  return onSnapshot(q, (snap) => {
    const chats = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    onUpdate(chats);
  }, onError);
}

/**
 * subscribeMySignal — Listener real-time sul PROPRIO documento utente.
 *
 * Sostituisce il polling: il server aggiorna `rtSignalAt` (su users/{uid}, che
 * le regole permettono di leggere SOLO al proprietario) quando arriva un nuovo
 * messaggio, una lettura o un unsend che ci riguarda. Qui ascoltiamo quel campo
 * e invochiamo `onTick(millis)` ad ogni cambiamento → il chiamante fa UNA fetch.
 *
 * Un listener idle costa ~0: niente più letture continue a orologio.
 * Ritorna la funzione di unsubscribe (no-op se manca myUid).
 *
 * @param {string} myUid  firebase uid del dispositivo corrente
 * @param {(millis:number)=>void} onTick  invocato col timestamp del segnale
 * @param {(err:Error)=>void} [onError]
 */
export function subscribeMySignal(myUid, onTick, onError) {
  if (!myUid) return () => {};
  const ref = doc(db(), 'users', myUid);
  return onSnapshot(
    ref,
    (snap) => {
      const v = snap.data()?.rtSignalAt;
      let t = 0;
      if (v && typeof v.toMillis === 'function') t = v.toMillis();
      else if (typeof v === 'number') t = v;
      onTick(t);
    },
    onError || (() => {}),
  );
}

/**
 * Marca un messaggio come visto (attiva TTL).
 */
export async function markViewed(chatId, msgId) {
  const fn = callFn('markMessageViewed');
  const res = await fn({ chatId, msgId });
  return res.data;
}

/**
 * Accetta una chat pending.
 */
export async function acceptChatRequest(chatId, addToWhitelist = false) {
  const fn = callFn('acceptChat');
  const res = await fn({ chatId, addToWhitelist });
  return res.data;
}

/**
 * Annulla un proprio messaggio NON ancora letto (mittente only, viewedAt null).
 * Cancella anche il blob allegato. Dopo la lettura non è più annullabile.
 */
export async function unsendMessage(chatId, msgId) {
  const fn = callFn('unsendMessage');
  const res = await fn({ chatId, msgId });
  return res.data;
}

/**
 * Rimuovi un proprio messaggio SOLO dalla mia chat (senza toccare la copia
 * del destinatario). Disponibile anche dopo la lettura. Per i miei messaggi.
 */
export async function deleteSelfCopy(chatId, msgId) {
  const fn = callFn('deleteSelfCopy');
  const res = await fn({ chatId, msgId });
  return res.data;
}

/**
 * Nasconde una chat dalla MIA vista (mode:'self') o la cancella per
 * ENTRAMBI (mode:'both'). 'both' cancella anche tutti i messaggi e blob.
 */
export async function hideChat(chatId, mode = 'self') {
  const fn = callFn('hideChat');
  const res = await fn({ chatId, mode });
  return res.data;
}

/**
 * Imposta la modalità inbox: 'request' | 'whitelist' | 'open' (TUONO).
 */
export async function setAcceptMode(mode) {
  const res = await callFn('setAcceptMode')({ mode });
  const d = res?.data;
  if (!d?.ok) throw new Error(d?.error || 'setAcceptMode fallito');
  return d;
}

/**
 * Cerca un sigil (silenzioso).
 */
export async function searchSigil(sigil) {
  const fn = callFn('searchSigil');
  const res = await fn({ sigil });
  return res.data;
}

/**
 * Pubblica le proprie chiavi sul server.
 */
export async function publishMyKeys(pubKeySignB64, pubKeyBoxB64) {
  const fn = callFn('publishKeys');
  const res = await fn({ pubKeySign: pubKeySignB64, pubKeyBox: pubKeyBoxB64 });
  return res.data;
}

/**
 * Pubblica le chiavi pubbliche a partire dalle keypair (Uint8Array).
 * Va chiamata a OGNI stabilimento di sessione (primo claim + tre porte di
 * re-login): senza pubKeyBox sul server l'utente e' irraggiungibile e
 * findUserBySigil risponde "user has no pubKeyBox published yet".
 * Idempotente lato server (publishKeys fa upsert).
 */
export async function publishMyKeysFromKeypairs(signPubBytes, boxPubBytes) {
  const [pubKeySign, pubKeyBox] = await Promise.all([
    toBase64Url(signPubBytes),
    toBase64Url(boxPubBytes),
  ]);
  return publishMyKeys(pubKeySign, pubKeyBox);
}

/**
 * Auto-recupero: assicura che la mappa sigil→uid esista sul server.
 *
 * Se la mappa `sigils/{nome}` manca (es. dopo un wipe lato server), la
 * ricrea verso l'uid corrente — che è deterministico dalla mnemonica, quindi
 * "ri-registrare" restituisce all'utente lo STESSO sigil/identità e lo rende
 * di nuovo trovabile dagli altri. Se il sigil è già registrato (caso normale)
 * il server risponde SIGIL_TAKEN / SIGIL_ALREADY_SET e qui lo ignoriamo.
 *
 * Prima CONTROLLA con findUserBySigil (lettura, 200): se la mappa esiste già
 * (caso normale) non fa nulla — così evita il 409 di registerSigil che il
 * browser loggherebbe come errore. Ri-registra solo se la mappa manca.
 *
 * Inoltre, "ultimo dispositivo vince": se la mappa punta a un ALTRO device
 * (stessa identità), chiama registerSigil che ri-punta il sigil a questo
 * dispositivo (il server verifica la pubKeySign). Così accedi col tuo sigil
 * da qualsiasi device e quello diventa l'attivo, senza sync delle chat.
 *
 * @param {string} sigil
 * @param {string} myFirebaseUid - uid corrente per riconoscere "è mio"
 * @returns {Promise<'restored'|'present'|'takeover'|'taken-by-other'|'error'>}
 */
export async function ensureSigilRegistered(sigil, myFirebaseUid) {
  if (!sigil) return 'error';
  try {
    const find = callFn('findUserBySigil');
    const res = await find({ sigil });
    const d = res.data || {};
    // Mappa già verso di me → niente da fare.
    if (d.uid && d.uid === myFirebaseUid) return 'present';
    // Mappa assente (wipe) o verso un altro device (stessa identità):
    // registerSigil la (ri)crea/ri-punta a me. Se è davvero di un altro,
    // il server risponde SIGIL_TAKEN e qui lo trattiamo come 'taken-by-other'.
    const reg = callFn('registerSigil');
    await reg({ sigil });
    return d.uid ? 'takeover' : 'restored';
  } catch (err) {
    const msg = (err?.message || '').toLowerCase();
    if (msg.includes('taken')) return 'taken-by-other';
    console.warn('ensureSigilRegistered:', err?.message || err);
    return 'error';
  }
}

/**
 * Key pinning TOFU (Trust On First Use) — difesa anti-MITM lato client.
 *
 * La chiave pubblica dell'interlocutore arriva dal server; un server
 * malevolo potrebbe sostituirla per intercettare. Qui memorizziamo la chiave
 * vista al PRIMO contatto e, alle volte successive, segnaliamo se cambia.
 * Un cambio di chiave NON dovrebbe mai accadere (le chiavi sono derivate dal
 * seed e immutabili): se cambia, è sospetto.
 *
 * @returns {'first'|'ok'|'changed'}
 */
export function pinPeerKey(uid, pubKeyBox) {
  if (!uid || !pubKeyBox) return 'first';
  const key = `nullum.keypin.v1.${uid}`;
  try {
    const seen = localStorage.getItem(key);
    if (!seen) {
      localStorage.setItem(key, pubKeyBox);
      return 'first';
    }
    return seen === pubKeyBox ? 'ok' : 'changed';
  } catch {
    return 'first';
  }
}

/**
 * Codice di sicurezza verificabile (safety number) — punto 2, parte 2.
 *
 * Deriva un codice numerico DETERMINISTICO dalle due chiavi pubbliche box
 * (ordinate, così è identico per entrambi). Se due utenti leggono lo stesso
 * codice (a voce / di persona), hanno la certezza che nessun server abbia
 * sostituito le chiavi (no MITM). Usa SHA-256 (Web Crypto, niente dipendenze).
 *
 * @returns {Promise<string>} 12 cifre in 3 gruppi, es. "8421 0937 5560"
 */
export async function computeSafetyCode(myBoxPubB64, otherBoxPubB64) {
  if (!myBoxPubB64 || !otherBoxPubB64) return '';
  const material = [myBoxPubB64, otherBoxPubB64].sort().join('|');
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(material));
  const bytes = new Uint8Array(buf);
  let digits = '';
  for (let i = 0; i < 12; i++) digits += (bytes[i] % 10).toString();
  return digits.replace(/(\d{4})(\d{4})(\d{4})/, '$1 $2 $3');
}

/**
 * Crea un invite link C2C opaco.
 * @param {object} opts
 * @param {number} opts.ttlHours 24|72|168 (default 24)
 * @param {'full'|'request'|'restricted'} opts.whitelistLevel (default request)
 * @param {boolean} opts.multiUse (default false = monouso)
 */
export async function createInvite({ ttlMinutes, ttlHours = 24, whitelistLevel = 'request', multiUse = false, emitAsSigil = null, asBetaTester = false, requirePasscode = false } = {}) {
  const fn = callFn('createInvite');
  const payload = ttlMinutes != null
    ? { ttlMinutes, whitelistLevel, multiUse }
    : { ttlHours, whitelistLevel, multiUse };
  if (emitAsSigil) payload.emitAsSigil = emitAsSigil;
  if (asBetaTester) payload.asBetaTester = true;
  if (requirePasscode) payload.requirePasscode = true;
  const res = await fn(payload);
  return res.data; // { ok, token, url, expiresAt, passcode }
}

/**
 * Riscatta un invite token.
 */
export async function redeemInvite(token, passcode = null, { dryRun = false } = {}) {
  const fn = callFn('redeemInvite');
  const payload = { token };
  if (passcode) payload.passcode = passcode;
  if (dryRun) payload.dryRun = true;
  const res = await fn(payload);
  return res.data;
}

/**
 * Elenco dei MIEI inviti (creati da me), con stato attivo/usato/scaduto/revocato.
 * @returns {Promise<Array>} array di inviti (vuoto se errore)
 */
export async function listMyInvites() {
  try {
    const res = await callFn('listMyInvites')({});
    return res?.data?.ok ? (res.data.invites || []) : [];
  } catch {
    return [];
  }
}

/**
 * Revoca un mio invito: chi non l'ha ancora accettato non potrà più usarlo.
 */
export async function revokeInvite(token) {
  const res = await callFn('revokeInvite')({ token });
  const d = res?.data;
  if (!d?.ok) throw new Error(d?.error || 'revoca fallita');
  return d;
}

/**
 * Cancella definitivamente un mio invito (per ripulire scaduti/usati).
 */
export async function deleteInvite(token) {
  const res = await callFn('deleteInvite')({ token });
  const d = res?.data;
  if (!d?.ok) throw new Error(d?.error || 'cancellazione fallita');
  return d;
}

/**
 * Conferma o rifiuta un riscatto in attesa di un mio invito monouso.
 * @param {string} token  il token dell'invito riscattato
 * @param {boolean} accept true = chi ha riscattato diventa contatto pre-approvato;
 *                         false = rifiutato (nessuna connessione).
 */
export async function confirmRedemption(token, accept) {
  const res = await callFn('confirmRedemption')({ token, accept: accept === true });
  const d = res?.data;
  if (!d?.ok) throw new Error(d?.error || 'conferma fallita');
  return d;
}

/**
 * Legge la propria whitelist (contatti pre-approvati).
 * @param {string} myFirebaseUid
 * @returns {Promise<Array<{contactUid, sigil?, level?, viaInvite?}>>}
 */
export async function getMyWhitelist(myFirebaseUid) {
  // Preferisci la callable che arricchisce col sigil CORRENTE (gestisce le
  // voci create prima che il contatto avesse un sigil). Fallback al read
  // diretto se la callable non risponde.
  try {
    const res = await callFn('listMyWhitelist')({});
    if (res?.data?.ok) return res.data.entries || [];
  } catch (e) { console.warn('listMyWhitelist failed, fallback', e); }
  if (!myFirebaseUid) return [];
  const snap = await getDocs(collection(db(), 'users', myFirebaseUid, 'whitelist'));
  return snap.docs.map((d) => ({ contactUid: d.id, ...d.data() }));
}

/**
 * Rimuove un contatto dalla propria whitelist.
 */
export async function removeWhitelist(contactUid) {
  const fn = callFn('removeWhitelist');
  return (await fn({ contactUid })).data;
}

/**
 * Aggiunge manualmente un sigil alla propria whitelist.
 */
export async function addToWhitelist(sigil, level = 'full') {
  const fn = callFn('addToWhitelist');
  const res = await fn({ sigil, level });
  if (!res?.data?.ok) throw new Error(res?.data?.error || 'add failed');
  return res.data;
}

/**
 * Carica profilo pubblico di un altro utente.
 */
export async function loadOtherUser(uid) {
  const snap = await getDoc(doc(db(), 'users', uid));
  if (!snap.exists()) return null;
  return snap.data();
}
